/**
 * Schema serializer for homoiconic storage.
 *
 * Converts a GraphDef to a SerializedSchema for database storage.
 * Uses Zod's toJSONSchema() for property schema serialization.
 */
import { z } from "zod";

import { captureWherePredicate } from "../constraints";
import {
  getEdgeKinds,
  getNodeKinds,
  type GraphDef,
} from "../core/define-graph";
import { isEdgeTargetMap, projectTargetKinds } from "../core/edge-endpoints";
import { canonicalAnnotations } from "../core/json-value";
import {
  type EdgeRegistration,
  type NodeRegistration,
  type NullCheckOp,
  type UniqueConstraint,
} from "../core/types";
import { ConfigurationError } from "../errors";
import {
  type GraphExtension,
  LEGACY_GRAPH_EXTENSION_VERSION,
} from "../graph-extension/extension-types";
import {
  type EdgeIndexDeclaration,
  type IndexDeclaration,
  type NodeIndexDeclaration,
  type VectorIndexDeclaration,
} from "../indexes/types";
import {
  getTypeName,
  type MetaEdge,
  type OntologyRelation,
} from "../ontology/types";
import { computeClosuresFromOntology } from "../registry/kind-registry";
import { nowIso } from "../utils/date";
import { sha256Hex } from "../utils/hash";
import { createDataKeyedBag } from "../utils/object";
import { sortedReplacer } from "./canonical";
import {
  type JsonSchema,
  type SchemaHash,
  type SerializedClosures,
  type SerializedEdgeDef,
  type SerializedMetaEdge,
  type SerializedNodeDef,
  type SerializedOntology,
  type SerializedOntologyRelation,
  type SerializedSchema,
  type SerializedUniqueConstraint,
} from "./types";

// ============================================================
// Main Serialization
// ============================================================

/**
 * Serializes a GraphDef to a SerializedSchema.
 *
 * @param graph - The graph definition to serialize
 * @param version - The schema version number
 * @returns The serialized schema
 */
export function serializeSchema<G extends GraphDef>(
  graph: G,
  version: number,
): SerializedSchema {
  const nodes = serializeNodes(graph);
  const edges = serializeEdges(graph);
  const ontology = serializeOntology(graph.ontology);
  const indexes = serializeIndexes(graph.indexes);
  const extension = canonicalExtension(graph.extension);
  const deprecatedKinds = serializeDeprecatedKinds(graph.deprecatedKinds);
  const annotations = canonicalAnnotations(graph.annotations);

  return {
    graphId: graph.id,
    ...(annotations === undefined ? {} : { annotations }),
    version,
    generatedAt: nowIso(),
    nodes,
    edges,
    ontology,
    defaults: {
      onNodeDelete: graph.defaults.onNodeDelete,
      temporalMode: graph.defaults.temporalMode,
    },
    ...(graph.identity === undefined ? {} : { identity: graph.identity }),
    // `serializeIndexes` collapses both absent and empty inputs to
    // `undefined` (see the helper's docstring); omit the field entirely
    // in that case so legacy graphs hash byte-identically to the
    // pre-`indexes` form.
    ...(indexes === undefined ? {} : { indexes }),
    // The graph extension is the durable source the loader uses to
    // rebuild extension-kind Zod validators on restart. Omitted on
    // graphs that have never been extended so legacy schemas hash
    // byte-identically.
    ...(extension === undefined ? {} : { extension }),
    // Soft-deprecated kind names. Omitted when empty so legacy
    // schemas hash byte-identically; sorted for canonical-form
    // stability across insertion-order differences.
    ...(deprecatedKinds === undefined ? {} : { deprecatedKinds }),
  };
}

const SERIALIZED_SCHEMA_TOP_LEVEL_KEYS = new Set([
  "graphId",
  "annotations",
  "version",
  "generatedAt",
  "nodes",
  "edges",
  "ontology",
  "defaults",
  "identity",
  "indexes",
  "extension",
  "deprecatedKinds",
]);

/**
 * Serializes a graph while retaining top-level fields authored by a newer
 * schema-document format.
 *
 * Known fields always come from the current graph, including omission of
 * optional slices. Only fields this version does not own are carried forward.
 * Every recommit path uses this function so parsing a newer document can never
 * turn a later, otherwise-valid schema write into silent metadata loss.
 */
export function serializeSchemaPreservingUnknownFields<G extends GraphDef>(
  graph: G,
  version: number,
  previous: SerializedSchema,
): SerializedSchema {
  const unknownFields = Object.fromEntries(
    Object.entries(previous).filter(
      ([key]) => !SERIALIZED_SCHEMA_TOP_LEVEL_KEYS.has(key),
    ),
  );
  return { ...unknownFields, ...serializeSchema(graph, version) };
}

function serializeDeprecatedKinds(
  set: ReadonlySet<string> | undefined,
): readonly string[] | undefined {
  if (set === undefined || set.size === 0) return undefined;
  return [...set].toSorted();
}

/**
 * Strips `version` from the persisted extension when it equals
 * the legacy default (a stable `1`). The version is metadata about
 * the document format, not semantic schema content; omitting the
 * default value from the canonical form means documents persisted
 * before the version field existed (no `version`) hash byte-identically
 * with documents persisted after (`version: 1`).
 *
 * Pinned to `LEGACY_GRAPH_EXTENSION_VERSION` rather than
 * `CURRENT_GRAPH_EXTENSION_VERSION` so future major bumps don't
 * silently re-classify already-stored documents — when v2 ships,
 * v1 documents continue to omit `version` (because `1 ===
 * LEGACY`), and v2 documents emit `version: 2` explicitly. The
 * canonical-form rule stays stable across library versions.
 */
function canonicalExtension(
  document: GraphExtension | undefined,
): GraphExtension | undefined {
  if (document === undefined) return undefined;
  if (document.version === undefined) return document;

  if (document.version !== LEGACY_GRAPH_EXTENSION_VERSION) return document;
  const { version: _omit, ...rest } = document;
  return rest;
}

// ============================================================
// Index Serialization
// ============================================================

/**
 * Returns the canonical-form `indexes` slice for the schema document, or
 * `undefined` when the input graph never declared the slice (so the
 * field is omitted entirely).
 *
 * Canonical-form rules (load-bearing for hash stability):
 * - `origin: "compile-time"` is the default and is omitted from the
 *   serialized form. Only `origin: "runtime"` is emitted explicitly so
 *   the loader can route the declaration through the runtime compiler on
 *   restart.
 *
 * The slice is order-canonicalized (sorted by name) and collapses both
 * `undefined` and an empty array to "no slice" so the hash and the diff
 * agree: indexes are an unordered set keyed by name, and an empty list
 * carries no semantic meaning that an absent slice doesn't.
 */
function serializeIndexes(
  indexes: readonly IndexDeclaration[] | undefined,
): readonly IndexDeclaration[] | undefined {
  if (indexes === undefined || indexes.length === 0) return undefined;
  return indexes
    .toSorted((a, b) =>
      a.name < b.name ? -1
      : a.name > b.name ? 1
      : 0,
    )
    .map((index) => serializeIndexDeclaration(index));
}

/**
 * Canonicalizes a single index declaration to its serialized form (see
 * {@link serializeIndexes} for the canonical-form rules). Exported so
 * consumers that hash a declaration directly — e.g.
 * `computeIndexSignature` in `store/materialize-indexes.ts` — can
 * normalize it first, rather than hashing the raw object and risking a
 * present-but-canonically-absent field (like an explicit `keySystemColumns:
 * []`) producing a different hash than the omitted form.
 */
export function serializeIndexDeclaration(
  declaration: IndexDeclaration,
): IndexDeclaration {
  if (declaration.entity === "node") {
    return serializeNodeIndexDeclaration(declaration);
  }
  if (declaration.entity === "edge") {
    return serializeEdgeIndexDeclaration(declaration);
  }
  return serializeVectorIndexDeclaration(declaration);
}

function serializeVectorIndexDeclaration(
  declaration: VectorIndexDeclaration,
): VectorIndexDeclaration {
  return {
    entity: "vector",
    kind: declaration.kind,
    name: declaration.name,
    fieldPath: declaration.fieldPath,
    dimensions: declaration.dimensions,
    metric: declaration.metric,
    indexType: declaration.indexType,
    indexParams: declaration.indexParams,
    ...(declaration.origin === "runtime" ? { origin: "runtime" as const } : {}),
  };
}

function serializeNodeIndexDeclaration(
  declaration: NodeIndexDeclaration,
): NodeIndexDeclaration {
  return {
    entity: "node",
    kind: declaration.kind,
    name: declaration.name,
    fields: declaration.fields,
    fieldValueTypes: declaration.fieldValueTypes,
    coveringFields: declaration.coveringFields,
    coveringFieldValueTypes: declaration.coveringFieldValueTypes,
    unique: declaration.unique,
    scope: declaration.scope,
    where: declaration.where,
    // `method: "btree"` is canonicalized by absence, like `origin` below.
    ...(declaration.method === undefined ? {} : { method: declaration.method }),
    // `keySystemColumns: []` is canonicalized by absence, same rule. Checked
    // against length, not just `undefined` — `NodeIndexDeclaration` is
    // exported and `defineGraph` accepts pre-built declarations directly
    // (bypassing `defineNodeIndex`'s own canonicalization and the
    // `nodeIndexDeclarationZod` parse boundary, which rejects an explicit
    // `[]` via `.min(1)`), so an in-memory declaration can still carry a
    // present-but-empty array that must canonicalize to absent here.
    ...((
      declaration.keySystemColumns === undefined ||
      declaration.keySystemColumns.length === 0
    ) ?
      {}
    : { keySystemColumns: declaration.keySystemColumns }),
    ...(declaration.keys === undefined || declaration.keys.length === 0 ?
      {}
    : { keys: declaration.keys }),
    // `origin: "compile-time"` is the default and is omitted from the
    // canonical form (absence == compile-time). Only `runtime` is
    // emitted explicitly so the restart loader can route the declaration
    // through the runtime compiler.
    ...(declaration.origin === "runtime" ? { origin: "runtime" as const } : {}),
  };
}

function serializeEdgeIndexDeclaration(
  declaration: EdgeIndexDeclaration,
): EdgeIndexDeclaration {
  return {
    entity: "edge",
    kind: declaration.kind,
    name: declaration.name,
    direction: declaration.direction,
    fields: declaration.fields,
    fieldValueTypes: declaration.fieldValueTypes,
    coveringFields: declaration.coveringFields,
    coveringFieldValueTypes: declaration.coveringFieldValueTypes,
    unique: declaration.unique,
    scope: declaration.scope,
    where: declaration.where,
    // `method: "btree"` is canonicalized by absence, like `origin`.
    ...(declaration.method === undefined ? {} : { method: declaration.method }),
    ...(declaration.origin === "runtime" ? { origin: "runtime" as const } : {}),
  };
}

// ============================================================
// Node Serialization
// ============================================================

/**
 * Serializes all node definitions.
 */
function serializeNodes<G extends GraphDef>(
  graph: G,
): Record<string, SerializedNodeDef> {
  const result = createDataKeyedBag<SerializedNodeDef>();

  for (const kindName of getNodeKinds(graph)) {
    const registration = graph.nodes[kindName];
    if (registration === undefined) continue;
    result[kindName] = serializeNodeDef(registration);
  }

  // Spread at the boundary: this becomes `SerializedSchema.nodes`, which
  // `serializeSchema` returns. See `createDataKeyedBag` in ../utils/object.ts.
  return { ...result };
}

/**
 * Serializes a single node registration.
 */
function serializeNodeDef(registration: NodeRegistration): SerializedNodeDef {
  const node = registration.type;
  const annotations = canonicalAnnotations(node.annotations);

  return {
    kind: node.kind,
    properties: serializeZodSchema(node.schema),
    uniqueConstraints: serializeUniqueConstraints(registration.unique ?? []),
    onDelete: registration.onDelete ?? "restrict",
    description: node.description,
    ...(annotations === undefined ? {} : { annotations }),
  };
}

/**
 * Serializes unique constraints.
 */
function serializeUniqueConstraints(
  constraints: readonly UniqueConstraint[],
): readonly SerializedUniqueConstraint[] {
  return constraints.map((constraint) => ({
    name: constraint.name,
    fields: [...constraint.fields],
    where:
      constraint.where ? serializeWherePredicate(constraint.where) : undefined,
    scope: constraint.scope,
    collation: constraint.collation,
  }));
}

/**
 * A serialized predicate structure (matches UniqueConstraintPredicate from core/types).
 */
type SerializedPredicate = Readonly<{
  __type: "unique_predicate";
  field: string;
  op: NullCheckOp;
}>;

/**
 * Field builder returned by the predicate proxy.
 */
type FieldPredicateBuilder = Readonly<{
  isNull: () => SerializedPredicate;
  isNotNull: () => SerializedPredicate;
}>;

/**
 * Predicate builder type for where clause serialization.
 */
type PredicateBuilder = Readonly<Record<string, FieldPredicateBuilder>>;

/**
 * Serializes a where predicate function to a JSON-serializable structure.
 *
 * Capture runs through `captureWherePredicate` — the same builder constraint
 * EVALUATION and definition-time VALIDATION use — rather than a second proxy
 * spelled here. Two builders answering the same question is how a persisted
 * `where` drifts from the one the store enforces.
 *
 * No field validation happens here: `defineGraph` refuses an undeclared `where`
 * field before a constraint can be registered (`assertWhereFieldDeclared`), and
 * `validateGraphExtension` refuses the same thing for document-declared kinds,
 * so by the time a graph is serialized its clauses are already declared-field
 * clauses. Re-checking would make persistence a second owner of a decision that
 * has one.
 */
function serializeWherePredicate(
  whereFunction: (builder: PredicateBuilder) => SerializedPredicate,
): string {
  const predicate = captureWherePredicate(whereFunction);
  if (predicate === undefined) {
    throw new ConfigurationError(
      `A unique constraint's \`where\` callback did not return a predicate, so it cannot be persisted.`,
      {},
      {
        suggestion: `Return a field predicate, e.g. \`where: (fields) => fields.someField.isNotNull()\`.`,
      },
    );
  }

  // Serialize the predicate structure as JSON
  return JSON.stringify({ field: predicate.field, op: predicate.op });
}

/**
 * Deserializes a where predicate JSON back to a predicate function.
 *
 * This can be used to reconstruct a UniqueConstraint's where clause
 * from a serialized schema.
 *
 * @param serialized - The JSON string from serialization
 * @returns A where function that returns the predicate structure
 */
/**
 * Unique predicate result type.
 */
type UniquePredicate = Readonly<{
  __type: "unique_predicate";
  field: string;
  op: NullCheckOp;
}>;

export function deserializeWherePredicate(
  serialized: string,
): (builder: PredicateBuilder) => UniquePredicate {
  const parsed = JSON.parse(serialized) as {
    field: string;
    op: NullCheckOp;
  };

  return (builder: PredicateBuilder): UniquePredicate => {
    const fieldBuilder = builder[parsed.field];
    if (!fieldBuilder) {
      throw new Error(`Unknown field in where predicate: ${parsed.field}`);
    }

    const result =
      parsed.op === "isNull" ? fieldBuilder.isNull() : fieldBuilder.isNotNull();
    return {
      __type: "unique_predicate",
      field: result.field,
      op: result.op,
    };
  };
}

// ============================================================
// Edge Serialization
// ============================================================

/**
 * Serializes all edge definitions.
 */
function serializeEdges<G extends GraphDef>(
  graph: G,
): Record<string, SerializedEdgeDef> {
  const result = createDataKeyedBag<SerializedEdgeDef>();

  for (const kindName of getEdgeKinds(graph)) {
    const registration = graph.edges[kindName];
    if (registration === undefined) continue;
    result[kindName] = serializeEdgeDef(registration);
  }

  // Spread at the boundary: this becomes `SerializedSchema.edges`.
  return { ...result };
}

/**
 * Serializes a single edge registration.
 */
function serializeEdgeDef(registration: EdgeRegistration): SerializedEdgeDef {
  const edge = registration.type;
  const annotations = canonicalAnnotations(edge.annotations);

  const isMap = isEdgeTargetMap(registration.to);
  const toKinds =
    isMap ?
      projectTargetKinds(registration.to).toSorted()
    : registration.to.map((node) => node.kind);

  let targetKindsBySource: Record<string, readonly string[]> | undefined;
  if (isMap) {
    const map = createDataKeyedBag<readonly string[]>();
    for (const [sourceKind, targets] of Object.entries(registration.to)) {
      map[sourceKind] = targets.map((t) => t.kind).toSorted();
    }
    targetKindsBySource = { ...map };
  }

  return {
    kind: edge.kind,
    fromKinds: registration.from.map((node) => node.kind),
    toKinds,
    ...(targetKindsBySource === undefined ? {} : { targetKindsBySource }),
    properties: serializeZodSchema(edge.schema),
    cardinality: registration.cardinality ?? "many",
    endpointExistence: registration.endpointExistence ?? "notDeleted",
    ...(registration.matchIdentity === undefined ?
      {}
    : {
        matchIdentity: {
          name: registration.matchIdentity.name,
          fields: [...registration.matchIdentity.fields],
        },
      }),
    description: edge.description,
    ...(annotations === undefined ? {} : { annotations }),
  };
}

// ============================================================
// Ontology Serialization
// ============================================================

/**
 * Serializes the complete ontology.
 */
function serializeOntology(
  relations: readonly OntologyRelation[],
): SerializedOntology {
  // Collect unique meta-edges
  const metaEdgeMap = new Map<string, MetaEdge>();
  for (const relation of relations) {
    const metaEdge = relation.metaEdge;
    if (!metaEdgeMap.has(metaEdge.name)) {
      metaEdgeMap.set(metaEdge.name, metaEdge);
    }
  }

  // Serialize meta-edges
  // Data-keyed: meta-edge names.
  const metaEdges = createDataKeyedBag<SerializedMetaEdge>();
  for (const [name, metaEdge] of metaEdgeMap) {
    metaEdges[name] = serializeMetaEdge(metaEdge);
  }

  // Serialize relations
  const serializedRelations = relations.map((relation) =>
    serializeOntologyRelation(relation),
  );

  // Compute and serialize closures
  const closures = serializeClosures(relations);

  return {
    metaEdges: { ...metaEdges },
    relations: serializedRelations,
    closures,
  };
}

/**
 * Serializes a meta-edge.
 */
function serializeMetaEdge(metaEdge: MetaEdge): SerializedMetaEdge {
  return {
    name: metaEdge.name,
    transitive: metaEdge.properties.transitive,
    symmetric: metaEdge.properties.symmetric,
    reflexive: metaEdge.properties.reflexive,
    inverse: metaEdge.properties.inverse,
    inference: metaEdge.properties.inference,
    description: metaEdge.properties.description,
  };
}

/**
 * Serializes an ontology relation.
 */
function serializeOntologyRelation(
  relation: OntologyRelation,
): SerializedOntologyRelation {
  return {
    metaEdge: relation.metaEdge.name,
    from: getTypeName(relation.from),
    to: getTypeName(relation.to),
  };
}

/**
 * Serializes precomputed closures.
 */
function serializeClosures(
  relations: readonly OntologyRelation[],
): SerializedClosures {
  if (relations.length === 0) {
    return {
      subClassAncestors: {},
      subClassDescendants: {},
      broaderClosure: {},
      narrowerClosure: {},
      equivalenceSets: {},
      disjointPairs: [],
      partOfClosure: {},
      hasPartClosure: {},
      iriToKind: {},
      edgeInverses: {},
      edgeImplicationsClosure: {},
      edgeImplyingClosure: {},
    };
  }

  const computed = computeClosuresFromOntology(relations);

  return {
    subClassAncestors: mapToRecord(computed.subClassAncestors),
    subClassDescendants: mapToRecord(computed.subClassDescendants),
    broaderClosure: mapToRecord(computed.broaderClosure),
    narrowerClosure: mapToRecord(computed.narrowerClosure),
    equivalenceSets: mapToRecord(computed.equivalenceSets),
    disjointPairs: [...computed.disjointPairs],
    partOfClosure: mapToRecord(computed.partOfClosure),
    hasPartClosure: mapToRecord(computed.hasPartClosure),
    iriToKind: mapToSimpleRecord(computed.iriToKind),
    edgeInverses: mapToSimpleRecord(computed.edgeInverses),
    edgeImplicationsClosure: mapToRecord(computed.edgeImplicationsClosure),
    edgeImplyingClosure: mapToRecord(computed.edgeImplyingClosure),
  };
}

/**
 * Converts a Map<string, Set<string>> to Record<string, string[]>.
 */
function mapToRecord(
  map: ReadonlyMap<string, ReadonlySet<string>>,
): Record<string, readonly string[]> {
  // Data-keyed: the map's keys are kind / meta-edge names.
  const result = createDataKeyedBag<readonly string[]>();
  for (const [key, values] of map) {
    result[key] = [...values];
  }
  // Spread at the boundary: the closures land in the returned
  // `SerializedOntology`. See `createDataKeyedBag` in ../utils/object.ts.
  return { ...result };
}

/**
 * Converts a Map<string, string> to Record<string, string>.
 */
function mapToSimpleRecord(
  map: ReadonlyMap<string, string>,
): Record<string, string> {
  return Object.fromEntries(map);
}

// ============================================================
// Zod Schema Serialization
// ============================================================

/**
 * Memoizes `z.toJSONSchema` results keyed by Zod schema identity.
 * `z.toJSONSchema` walks the entire schema tree on every call;
 * `Store.introspect()` and any poll-driven UI built on top of it
 * runs the conversion per kind per call. Schemas are immutable for
 * the lifetime of a `NodeType` / `EdgeType`, so the cache is sound;
 * `WeakMap` follows the schema's lifetime so there's no memory leak
 * when the registry is rebuilt by `evolve` / `removeKinds` /
 * `deprecateKinds` (the old registry's schemas are unreferenced and
 * the cache entries become eligible for GC alongside them).
 */
const SCHEMA_PROPERTIES_CACHE = new WeakMap<z.ZodType, JsonSchema>();

/**
 * Serializes a Zod schema to JSON Schema. Memoized — repeated calls
 * with the same schema reference return the cached output without
 * re-walking the tree.
 *
 * Uses Zod 4's toJSONSchema() method for conversion. Re-exported for
 * `store.introspect()` so the in-memory graph can produce the same
 * JSON-Schema view of properties that the persisted form carries.
 */
export function serializeSchemaProperties(schema: z.ZodType): JsonSchema {
  const cached = SCHEMA_PROPERTIES_CACHE.get(schema);
  if (cached !== undefined) return cached;
  const computed = serializeZodSchema(schema);
  SCHEMA_PROPERTIES_CACHE.set(schema, computed);
  return computed;
}

function serializeZodSchema(schema: z.ZodType): JsonSchema {
  try {
    // Zod 4 has toJSONSchema as a standard export
    const jsonSchema = z.toJSONSchema(schema);
    return jsonSchema as JsonSchema;
  } catch {
    // Fallback for schemas that can't be converted
    return { type: "object" };
  }
}

// ============================================================
// Schema Hashing
// ============================================================

/**
 * Per-graph schema-hash cache. Keyed on the frozen `GraphDef` reference
 * (identity-stable across calls because `defineGraph` freezes its
 * output). Inner Map carries one entry per `version` ever computed for
 * that graph — typically 1-2 entries per graph (current + next-pending).
 *
 * Cache hit skips both `serializeSchema` (the per-kind walk is cached
 * separately by `SCHEMA_PROPERTIES_CACHE`, but the top-level assembly +
 * `JSON.stringify(sortedReplacer)` walk is not) and the SHA-256 hash —
 * the dominant cost on `ensureSchema` boots that observe an unchanged
 * schema. The cache lives on a `WeakMap` so retired graphs (after
 * `evolve`/`removeKinds` produce a new one) become eligible for GC
 * alongside their entries.
 */
const SCHEMA_HASH_CACHE = new WeakMap<GraphDef, Map<number, SchemaHash>>();

/**
 * Cached `(graph, version) → SchemaHash` lookup. Use this when the hash
 * is the only thing the caller needs (the most common case in
 * `ensureSchema`'s "are we up to date?" path). On cache miss, this
 * serializes and hashes once, populates the cache, and returns.
 */
export async function getSchemaHash(
  graph: GraphDef,
  version: number,
): Promise<SchemaHash> {
  const perGraph = SCHEMA_HASH_CACHE.get(graph);
  const cached = perGraph?.get(version);
  if (cached !== undefined) return cached;
  const hash = await computeSchemaHash(serializeSchema(graph, version));
  if (perGraph === undefined) {
    SCHEMA_HASH_CACHE.set(graph, new Map([[version, hash]]));
  } else {
    perGraph.set(version, hash);
  }
  return hash;
}

/**
 * The schema-version slot used only to serialize a graph for its
 * {@link getGraphDefinitionHash}. The value is written into the serialized
 * document but {@link computeSchemaHash} deliberately excludes `version` (and
 * `generatedAt`), so the digest is blind to it.
 */
const DEFINITION_HASH_VERSION = 1;

/**
 * Deterministic, version-blind hash of a graph DEFINITION.
 *
 * A durable working copy must attest the caller's fork-time definition
 * identity even when it committed no schema row: the identity of a graph
 * definition does not depend on which schema version it happened to be
 * persisted under. This returns exactly the schema-content digest
 * {@link getSchemaHash} produces, at a fixed version slot, so two references
 * to the same definition hash identically regardless of the version argument
 * (which {@link computeSchemaHash} excludes) — and a graph that reuses another
 * graph's id but declares different nodes/edges/constraints hashes
 * differently.
 */
export async function getGraphDefinitionHash(
  graph: GraphDef,
): Promise<SchemaHash> {
  return getSchemaHash(graph, DEFINITION_HASH_VERSION);
}

/**
 * Computes a hash of the schema content for change detection.
 *
 * Excludes version and generatedAt since those change on every save.
 */
export async function computeSchemaHash(
  schema: SerializedSchema,
): Promise<SchemaHash> {
  // Create a hashable representation excluding dynamic fields
  // (version, generatedAt). Optional slices are included only when
  // set so legacy schemas without them hash byte-identically — the
  // per-slice canonical-form rules (e.g., `indexes` sort + origin
  // omission, `deprecatedKinds` sort) are applied at serialize time.
  const hashable = {
    graphId: schema.graphId,
    nodes: schema.nodes,
    edges: schema.edges,
    ontology: schema.ontology,
    defaults: schema.defaults,
    ...(schema.identity === undefined ? {} : { identity: schema.identity }),
    ...(schema.indexes === undefined ? {} : { indexes: schema.indexes }),
    ...(schema.extension === undefined ? {} : { extension: schema.extension }),
    ...(schema.deprecatedKinds === undefined ?
      {}
    : { deprecatedKinds: schema.deprecatedKinds }),
  };

  // Serialize with sorted keys for deterministic output
  const json = JSON.stringify(hashable, sortedReplacer);
  return sha256Hex(json);
}
